import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Check, Pencil, X } from "lucide-react";
import { toast } from "react-toastify";
import { scheduleDeferredDeleteWithUndo } from "@/lib/deferredDeleteWithUndo";
import { accessService, type Role } from "@/services/accessService";
import { Can } from "@/components/Can";
import { useUiStore } from "@/store/uiStore";
import { useSettingsStore } from "@/store/settingsStore";

export function RolesTab() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const { startPageLoading, stopPageLoading } = useUiStore();
  const { legalEntityId } = useSettingsStore();

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalRoleName, setModalRoleName] = useState<string | null>(null);
  const [modalName, setModalName] = useState("");
  const [modalDesc, setModalDesc] = useState("");
  const [modalSaving, setModalSaving] = useState(false);

  const [confirmState, setConfirmState] = useState<{
    message: string;
    onConfirm: () => void;
  } | null>(null);

  const isEditing = modalRoleName !== null;

  const load = () =>
    accessService.listRoles().then(setRoles).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openCreateModal = () => {
    setModalRoleName(null);
    setModalName("");
    setModalDesc("");
    setModalOpen(true);
  };

  const openEditModal = (r: Role) => {
    setModalRoleName(r.name);
    setModalName(r.name);
    setModalDesc(r.description ?? "");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRoleName(null);
    setModalName("");
    setModalDesc("");
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (confirmState) setConfirmState(null);
      else if (modalOpen) closeModal();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [modalOpen, confirmState]);

  const handleModalSave = async () => {
    if (!modalName.trim()) return;
    setModalSaving(true);
    try {
      if (isEditing) {
        const updated = await accessService.updateRole(modalRoleName, {
          description: modalDesc.trim() || undefined,
        });
        setRoles((prev) => prev.map((r) => (r.name === modalRoleName ? updated : r)));
        toast.success(`Role "${modalName.trim()}" updated successfully`);
      } else {
        const role = await accessService.createRole({
          name: modalName.trim(),
          description: modalDesc.trim() || undefined,
          legalEntityId: legalEntityId ?? undefined,
        });
        setRoles((prev) => [...prev, role]);
        toast.success(`Role "${modalName.trim()}" created successfully`);
      }
      closeModal();
    } catch {
      toast.error(`Failed to ${isEditing ? "update" : "create"} role`);
    } finally {
      setModalSaving(false);
    }
  };

  const handleDelete = (name: string) => {
    const snapshot = roles.find((r) => r.name === name);
    if (!snapshot) return;

    const restoreToList = () => {
      setRoles((prev) => (prev.some((r) => r.name === name) ? prev : [...prev, snapshot]));
    };

    scheduleDeferredDeleteWithUndo({
      idKey: `role:${name}`,
      label: name,
      hint: "This role will be removed from Keycloak.",
      onScheduled: () => setRoles((prev) => prev.filter((r) => r.name !== name)),
      onUndo: restoreToList,
      onFailed: restoreToList,
      onCommit: () => accessService.deleteRole(name),
      onComplete: () => toast.success(`Role "${name}" deleted successfully`),
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {roles.length} role{roles.length !== 1 ? "s" : ""} in Keycloak
        </p>
        <Can permission="role:manage">
          <button
            id="create-role-btn"
            onClick={openCreateModal}
            className="flex items-center gap-2 rounded-xl bg-bridge-accent px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-bridge-accent/90"
          >
            <Plus size={14} /> New Role
          </button>
        </Can>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/80">
              <th className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Role</th>
              <th className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Description</th>
              <th className="px-5 py-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {roles.map((r) => (
              <tr key={r.name} className="transition-colors hover:bg-slate-50/70">
                <td className="px-5 py-3.5 font-semibold text-slate-900">{r.name}</td>
                <td className="px-5 py-3.5 text-slate-500">{r.description || "—"}</td>
                <td className="px-5 py-3.5 text-right">
                  <Can permission="role:manage">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => openEditModal(r)}
                        className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                        title="Edit role"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        id={`delete-role-${r.name}`}
                        onClick={() => handleDelete(r.name)}
                        className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                        title="Delete role"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {roles.length === 0 && (
          <p className="py-12 text-center text-sm text-slate-400">No roles found.</p>
        )}
      </div>

      {/* Create / Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fadeIn"
            onClick={closeModal}
            aria-hidden
          />
          <div
            className="relative flex w-full max-w-md flex-col overflow-hidden rounded-lg border border-bridge-border/70 bg-bridge-surface shadow-glass"
            role="dialog"
            aria-labelledby="role-modal-title"
          >
            <header className="flex items-center justify-between gap-3 border-b border-bridge-border/60 px-5 py-4">
              <h2
                id="role-modal-title"
                className="text-base font-semibold text-bridge-fg"
              >
                {isEditing ? "Edit role" : "Create new role"}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface-hover hover:text-bridge-fg"
              >
                <X size={16} />
              </button>
            </header>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-bridge-fg mb-1">
                  Role name
                </label>
                <input
                  id="new-role-name"
                  autoFocus
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && modalName.trim() && handleModalSave()
                  }
                  placeholder="e.g. Viewer"
                  disabled={isEditing}
                  className="w-full px-3 py-2 text-sm border border-bridge-border rounded-lg focus:outline-none focus:ring-2 focus:ring-bridge-accent/30 disabled:opacity-50 disabled:bg-bridge-bg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-bridge-fg mb-1">
                  Description
                </label>
                <input
                  id="new-role-desc"
                  value={modalDesc}
                  onChange={(e) => setModalDesc(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && modalName.trim() && handleModalSave()
                  }
                  placeholder="Optional description"
                  className="w-full px-3 py-2 text-sm border border-bridge-border rounded-lg focus:outline-none focus:ring-2 focus:ring-bridge-accent/30"
                />
              </div>
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-bridge-border/60 px-5 py-3">
              <button
                onClick={closeModal}
                className="px-3 py-1.5 text-sm text-bridge-muted hover:text-bridge-fg rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                id="save-role-btn"
                onClick={handleModalSave}
                disabled={modalSaving || !modalName.trim()}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-bridge-accent text-white text-sm font-medium rounded-lg disabled:opacity-50 hover:bg-bridge-accent/90 transition-colors"
              >
                {modalSaving ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Check size={13} />
                )}{" "}
                {isEditing ? "Update" : "Create"}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Confirm delete dialog */}
      {confirmState && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fadeIn"
            onClick={() => setConfirmState(null)}
            aria-hidden
          />
          <div
            className="relative flex w-full max-w-sm flex-col overflow-hidden rounded-lg border border-bridge-border/70 bg-bridge-surface shadow-glass"
            role="alertdialog"
            aria-labelledby="confirm-dialog-title"
          >
            <header className="flex items-center gap-3 border-b border-bridge-border/60 px-5 py-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50">
                <Trash2 size={16} className="text-red-500" />
              </div>
              <h2
                id="confirm-dialog-title"
                className="text-base font-semibold text-bridge-fg"
              >
                Confirm
              </h2>
            </header>
            <div className="px-5 py-4">
              <p className="text-sm text-bridge-fg">{confirmState.message}</p>
            </div>
            <footer className="flex items-center justify-end gap-2 border-t border-bridge-border/60 px-5 py-3">
              <button
                onClick={() => setConfirmState(null)}
                className="px-3 py-1.5 text-sm text-bridge-muted hover:text-bridge-fg rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmState.onConfirm}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 size={24} className="animate-spin text-bridge-accent" />
    </div>
  );
}
